import { Component, Input } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { TranslocoService } from "@jsverse/transloco";
import { NgbActiveModal, NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { GlobalService } from "app/global/global.service";
import { IGetCategoriesItemDto } from "app/main/pages/categories.dto";
import { CustomColumnService } from "app/main/pages/contract-control/custom-column-service/custom-column.service";
import { DocumentListingApiService } from "app/main/pages/documents/document-listing.api.service";
import { IDocumentTypesDto } from "app/main/pages/documents/dto/document-listing.dto";
import { DocumentListingFacade } from "app/main/pages/documents/store/document-listing.facade";
import { SharedStoreFacade } from "app/main/shared/shared/store/shared-store.facade";
import { cloneDeep } from 'lodash';
import { Observable, Subject, take } from "rxjs";
import * as categoriesSelectors from '../../../../../store/selectors/categories.selector';
import { DocumentFieldService } from "../../documentfields/documentfield.service";
import { MetaDataFormComponent } from "../../documentfields/meta-data-form/meta-data-form.component";
import { IMetadataItemDto, IMetadataListDTO, MetaDataTypeEnum } from "../../documentfields/metadata-item.dto";
import { DocumentTypeService } from "../document-type.service";
import { DocumentTypeFacade } from "../store/document-type.facade";
import { FieldSection } from "../utils/document-type.utils";
import { CustomBoxBuilderService } from "./contract-control-edit-layout/custom-box/custom-box-builder.service";
import { EditLayoutCallableViewEnum } from "./contract-control-edit-layout/edit-layout-callable.enum";
import { ContractControlDefaultColumns, DocumentDefaultColumns, DocumentTypeGroupEnum, DocumentTypeSectionEnum, IBoxTypeEnum, IFieldTypeEnum } from "./default-constants/document-type-settings.constants";
import { IGeneralSettingsForm } from "./document-type-settings-modal.component";
import { IBox, ICategory, IField, ISection } from "./document-type-settings.dto";
import { TabStateService } from "./tab-state.service";

interface ActiveColumnsSettings {
    document_type_setting: boolean;
    contract_type_setting: boolean;
}

interface LayoutSettingsResponse {
    categories?: string;
    document_preview?: string;
    document_columns?: string;
    contract_control_columns?: string;
    contract_control_preview?: string;
    document_pinned_fields?: string;
}

type MetadataSelectionItem = IMetadataItemDto & { type?: IFieldTypeEnum };

@Component({
    template: ''
})

export abstract class DocumentTypeAbstractComponent {

    @Input() public document_type_id!: number;

    public documentTypeSettings$!: Observable<IDocumentTypesDto>;
    public documentTypeSettings!: IDocumentTypesDto;
    public destroy$: Subject<void> = new Subject<void>();

    public generalSettingForm!: FormGroup;
    public activeTab!: string;
    public nestedActiveTab!: string;
    public isCollapsedGeneral: boolean = false;
    public isCollapsedContactMenu: boolean = true;
    public editLayoutCallableViewEnum = EditLayoutCallableViewEnum;
    public isCollapsedDocumentMenu: boolean = true;

    /* For searching fields */
    public autoCompleteLoading: boolean = false;
    public selectedMetaData!: MetadataSelectionItem;
    public metadata_keyword: string = 'meta_data_name';
    public metaDataItems: MetadataSelectionItem[] = [];
    public categories: IGetCategoriesItemDto[] = [];
    

    protected DocumentTypeSectionEnum = DocumentTypeSectionEnum;

    constructor(
        protected _activeModal: NgbActiveModal,
        protected _modalService: NgbModal,
        protected _coreSidebarService: CoreSidebarService,
        protected _globalService: GlobalService,
        protected _documentTypeService: DocumentTypeService,
        protected _documentListingApiService: DocumentListingApiService,
        protected _store: Store,
        protected _documentFieldService: DocumentFieldService,
        protected _documentFacade: DocumentListingFacade,
        protected _customColumnService: CustomColumnService,
        protected _transloco: TranslocoService,
        protected _formBuilder: FormBuilder,
        protected _sharedStoreFacade: SharedStoreFacade,
        protected _tabStateService: TabStateService,
        protected _documentTypeFacade: DocumentTypeFacade,
        protected _customBoxBuilderService: CustomBoxBuilderService
    ) { this.loadParentCategories(); }




    loadParentCategories() {
            // Subscribe to global categories store
        this._store.select(categoriesSelectors.flattenedCategories).pipe(take(1)).subscribe({
                next: (categories) => {
                    this.categories = categories || [];
                },
                error: (error) => {
                    console.error('Error loading categories from store:', error);
                }
            });
        }


    /*************************** Document Type Settings ***************************/



    /**
     * Gets the sections of the document type settings that correspond to the currently active tab.
     *
     * @returns An array of `ISection` objects whose `name` matches the active tab as determined by `_tabStateService`.
     */
    get activeTabSections(): ISection[] {
        return this.documentTypeSettings.sections.filter(x => x.name === this._tabStateService.getActiveTab());
    }




    /**
     * Calculates the total number of fields in the currently active nested tab across all sections.
     *
     * Iterates through each section in `activeTabSections`, filters groups by the active nested tab name,
     * and sums the number of fields in those groups.
     *
     * @returns The total count of fields in the active nested tab.
     */
    private getActiveTabFieldsOrder(): number {
        return (this.activeTabSections ?? []).reduce((total, section) => {
            const matchingGroups = section.groups?.filter(
                g => g.name === this._tabStateService.getActiveNestedTab()
            ) ?? [];
            return total + matchingGroups.reduce((sum, g) => sum + (g.fields?.length ?? 0), 0);
        }, 0);
    }









    /**
     * Calculates the total number of boxes across all sections in the currently active tab.
     *
     * Iterates through each section in `activeTabSections` and sums up the number of boxes
     * in each section to determine the overall box order.
     *
     * @returns {number} The total count of boxes in all active tab sections.
     */
    private getCustomBoxOrder(): number {
        const boxOrder = this.activeTabSections
            .reduce((total, section) => total + section.boxes?.filter(x => x.type === IBoxTypeEnum.CUSTOM)?.length, 0);

        return boxOrder + 1;
    }




    /**
     * Creates a new custom box and adds it to each section in the active tab, if a box with the same name does not already exist.
     * 
     * - Builds a custom box with default properties.
     * - Iterates over all sections in the active tab.
     * - Checks if a box with the name "Custom box" already exists in each section.
     *   - If it exists, displays an error message and skips adding the box to that section.
     *   - If it does not exist, adds the custom box to the section's boxes array.
     *
     * @remarks
     * Uses `buildBox` to construct the box object and `getCustomBoxOrder` to determine its order.
     * Displays an error using `_globalService.toastrError` if a duplicate is found.
     */
    createCustomBox(): void {
        const customBox = this._customBoxBuilderService.buildBox({
            id: null,
            name: `Custom box ${this.getCustomBoxOrder()}`,
            order: this.getCustomBoxOrder(),
            is_removeable: true,
            type: IBoxTypeEnum.CUSTOM,
            is_locked: false,
            status: true,
        });

        const customBoxName = customBox.name;
        const boxAlreadyExists = this.activeTabSections.some(section =>
            section.boxes?.some(box => box.name === customBoxName && box.type === IBoxTypeEnum.CUSTOM)
        );

        if (boxAlreadyExists) {
            this._globalService.toastrError('A box with this name already exists!');
            return;
        }

        this.activeTabSections.forEach(section => {
            const clonedBoxes = cloneDeep(section.boxes ?? []);
            clonedBoxes.push(cloneDeep(customBox));
            section.boxes = clonedBoxes;
        });

        this._documentTypeFacade.updateLayoutSettingsInState(
            cloneDeep(this.documentTypeSettings)
        );
    }



    addFieldsInCustomBox($event: { boxIndex: number, field: IMetadataItemDto }): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => {
            const box = section.boxes?.[$event.boxIndex];
            if (box) {
                box.fields = [...(box.fields || []), this._customBoxBuilderService.buildCustomField($event.field, $event.field.meta_data_type)] as IField[];
            }
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }



    removeFieldFromCustomBox($event: IMetadataItemDto): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => {
            section.boxes?.forEach(box => {
                if (box.fields) {
                    box.fields = box.fields.filter(f => f.meta_data_id !== $event.id);
                }
            });
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }



    updateCustomBoxName($event: { boxIndex: number, name: string }): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }

        this.activeTabSections.forEach(section => {
            section.boxes = section.boxes.map((box, index) => {
                if (index === $event.boxIndex) {
                    return {
                        ...box,
                        name: $event.name
                    };
                }
                return box;
            });
        });

        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }

    /**
     * Updates the status of a specific box within all active tab sections.
     * 
     * @param boxIndex - The index of the box to update within each section's boxes array.
     * @param status - The new status to set for the specified box.
     * 
     * If no active tab sections are found, displays an error message.
     * After updating the box status, triggers an update to the document type layout settings in the state.
     */
    updateDefaultBoxStatus(boxIndex: number, status: boolean): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => {
            section.boxes = section.boxes.map((box, index) => {
                if (index === boxIndex) {
                    return {
                        ...box,
                        status: status
                    };
                }
                return box;
            });
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }




    updateBoxesInState(boxes: IBox[]): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => section.boxes = boxes);
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }




    /**
     * Updates the fields of a specific group within the active tab sections in the state.
     *
     * @param nestedTab - The enum value representing the group to update.
     * @param fields - The array of fields to set for the specified group.
     *
     * If no active tab sections are found, displays an error message.
     * After updating the fields, triggers an update to the layout settings in the state.
     */
    updateFieldsInState(nestedTab: DocumentTypeGroupEnum, fields: IField[]): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => {
            section.groups.forEach(group => {
                if (group.name === nestedTab) {
                    group.fields = fields;
                }
            });
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }




    /**
     * Removes a custom box at the specified index from all sections in the active tab.
     * If no active tab sections are found, displays an error message.
     * After removal, updates the document type layout settings in the application state.
     *
     * @param boxIndex - The index of the box to remove from each section's boxes array.
     */
    removeCustomBox(boxIndex: number): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }

        this.activeTabSections.forEach(section => {
            section.boxes = section.boxes.filter((_, index) => index !== boxIndex);
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }








    /**
     * Removes a custom field from the currently active tab section's group at the specified index.
     * If no active tab sections are found, displays an error message.
     * After removal, updates the document type layout settings in the state.
     *
     * @param fieldIndex - The index of the field to remove from the active group's fields array.
     */
    removeCustomField(fieldIndex: number, groupName: string): void {
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }

        this.activeTabSections.forEach(section => {
            section.groups?.forEach(group => {
                if (group.name === groupName) {
                    group.fields.splice(fieldIndex, 1);
                }
            });
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }




    /**
      * Adds a custom field to the active section.
      * This method checks if the custom field already exists in the current section.
      * If it does, it shows an error message and returns early.
      * If not, it creates a new column object with the custom field details and adds it to the section.
      * 
      * @remarks
      * - Uses `this.activeSection` to determine which section to add the custom field to.
      * - The new column is created with properties like `type`, `label`, `order`, `doc_id`, etc.
      * - The method closes the modal after adding the custom field.
      * 
      * @returns {void}
      */
    addCustomField(): void {
        if (!this.selectedMetaData) {
            return;
        }

        const selectedType = this.selectedMetaData?.type;
        const isDefaultField = this.selectedMetaData.section === IFieldTypeEnum.DEFAULT || selectedType === IFieldTypeEnum.DEFAULT;
        const fieldType = isDefaultField ? IFieldTypeEnum.DEFAULT : this.getFieldType();
        const field = this._customBoxBuilderService.buildCustomField(this.selectedMetaData, fieldType);
        field.order = this.getActiveTabFieldsOrder();
        if (!this.activeTabSections?.length) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        const activeTab = this._tabStateService.getActiveTab();
        this.activeTabSections.forEach(section => {
            section.groups?.forEach(group => {
                if (group.name === this._tabStateService.getActiveNestedTab()) {
                    const isAlreadyExist = group.fields.some(existingField => existingField.meta_data_id === this.selectedMetaData.id);
                    if (isAlreadyExist) {
                        this._globalService.toastrError('This field is already added!');
                        return;
                    }
                    group.fields = [...group.fields, field];
                }
            });
            if ([DocumentTypeSectionEnum.ContractControlColumns, DocumentTypeSectionEnum.DocumentColumns].includes(activeTab as DocumentTypeSectionEnum)) {
                section.columns = [...section.columns, field];
            }
        });
        this._activeModal.close();
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }






    /**
     * Selects a metadata item and either adds it as a custom field or opens a modal to create a new one.
     * If the item has an ID of 0, it indicates that a new metadata item should be created.
     * Otherwise, it sets the selected item as the custom field object.
     *
     * @param item - The metadata item to select.
     * This method checks if the selected metadata item has an ID of 0, which indicates that it is a new item to be created.
     * If so, it closes the current modal and opens a new modal for creating a new
     * metadata item with the specified name and type.
     * If the item has a valid ID, it sets the `customFieldObject` to
     * the selected item, allowing it to be added as a custom field.
     * * @remarks
     * - Uses `NgbModal` to open a modal for creating new metadata items.
     * - Sets the `meta_data_name` and `meta_data_type` for the new item.
     * - The `section` is set to 'document_field', which indicates the context in
     * which the metadata item is being used.
     * - The result of the modal is handled to update the `customFieldObject` if
     * the user successfully creates a new metadata item.
     * 
     * @returns {void}
     */
    selectMetaData(item: MetadataSelectionItem) {
        if (item.id == 0 && !item.section) {
            const addToMetaData: NgbModalRef = this._modalService.open(MetaDataFormComponent, { backdrop: 'static' });
            addToMetaData.componentInstance.view = 'modal';
            addToMetaData.componentInstance.meta_data_name = item.meta_data_name;
            addToMetaData.componentInstance.meta_data_type = MetaDataTypeEnum.text;
            addToMetaData.componentInstance.section = this.getFieldType();
            addToMetaData.result.then((metadata: IMetadataItemDto) => {
                if (metadata) {
                    this.selectedMetaData = metadata;
                }
            });
        } else {
            this.selectedMetaData = item;
        }
    }





    /**
     * Clears the custom field values by resetting the `metaData` array to empty
     * and setting the `selectedField` to null.
     *
     * @remarks
     * This method is typically used to reset the state of the custom field selection
     * in the search meta data modal component.
     */
    clearCustomfieldValue(): void {
        this.selectedMetaData = null;
    }




    /**
     * Opens the Add Meta Data Field modal dialog for adding new metadata fields.
     *
     * Dynamically imports the `AddMetaDataFieldModalComponent` and opens it as a modal
     * with a static backdrop. Sets the modal's view to 'modal' and its section to
     * `FieldSection.DOCUMENT_FIELD`. Also updates the active nested tab state.
     *
     * @param nestedActiveTab - The identifier of the nested tab to set as active after opening the modal.
     * @returns A promise that resolves when the modal has been opened and the tab state updated.
     */
    async openAddMetaDataModal(nestedActiveTab?: string): Promise<void> {
        const { AddMetaDataFieldModalComponent } = await import('../document-type-settings-modal/add-meta-data-field-modal/add-meta-data-field-modal.component');
        const modalRef = this._modalService.open(AddMetaDataFieldModalComponent, { backdrop: 'static' });
        modalRef.componentInstance.view = 'modal';
        modalRef.componentInstance.section = FieldSection.DOCUMENT_FIELD;
        if (nestedActiveTab) {
            this._tabStateService.setActiveNestedTab(nestedActiveTab);
        }
    }




    /**
     * Custom search on fields based on the provided items and query.
     * This method filters the items based on the query string and returns matching results.
     * It also adds an option to create a new metadata field if the query is not empty.
     *
     * @param items - The array of items to search through.
     * @param query - The search query string.
     * 
     * @returns An array of filtered items with an option to add a new field.
     */
    customSearchOnMetaDataField(items: { id: number, meta_data_name: string, query?: string }[], query: string): { id: number; meta_data_name: string; query?: string; }[] {
        const results: { id: number, meta_data_name: string, query?: string }[] = [];
        items.forEach(
            (item) => {
                if ((item['meta_data_name']).toLowerCase().indexOf(query.toLowerCase()) > -1) {
                    results.push(item);
                }
            }
        );
        results.push({ id: 0, meta_data_name: query, query: `Add ${query == '' ? 'New field' : query} as meta data` });
        return results;
    }






    /**
     * Searches for fields based on the provided query string.
     * This method uses the `documentFieldService` to fetch fields that match the search query.
     * It sets the `autoCompleteLoading` to true while the request is in progress
     * and updates the `metaData` array with the fetched fields.
     *
     * @param $event - The search query string.
     * 
     * @returns {void}
     */
    onFieldsSearch($event: string): void {
        this.autoCompleteLoading = true;
        const section = this.getMetaDataSection();
        this._documentFieldService.documentfields('all', $event, `section=${section?.join(',')}`)
            .subscribe({
                next: (fields: IMetadataListDTO) => {
                    const activeTab = this._tabStateService.getActiveTab() as DocumentTypeSectionEnum;
                    let defaultColumnsToAdd: IMetadataItemDto[] = [];

                    if ([DocumentTypeSectionEnum.ContractControlColumns, DocumentTypeSectionEnum.DocumentColumns].includes(activeTab)) {
                        const defaultColumns = activeTab === DocumentTypeSectionEnum.ContractControlColumns ? ContractControlDefaultColumns : DocumentDefaultColumns;
                        defaultColumnsToAdd = defaultColumns.map(column => ({
                            api_attribute: column.field_name,
                            created_at: '',
                            id: 0,
                            is_required: 0,
                            meta_data_name: this._transloco.translate(column.label) ?? '',
                            meta_data_option: [],
                            meta_data_type: MetaDataTypeEnum.text,
                            section: 'default',
                            updated_at: '',
                            user_id: 0,
                            type: 'default',
                            workspace_id: 0,
                            ...column
                        }));
                    }
                    
                    
                    this.metaDataItems = [...(fields.data || []), ...defaultColumnsToAdd];

                    if(activeTab === DocumentTypeSectionEnum.DocumentColumns) {
                        // Search child categories (categories that have a parent_id)
                        const matchedCategories = this.categories.filter(category => 
                            category.category_name?.toLowerCase().includes($event.toLowerCase())
                        ).map(category => ({
                            api_attribute: `category_${category.id ?? 0}`,
                            created_at: category.created_at?.toString() ?? '',
                            field_name: `category_${category.id ?? 0}`,
                            id: category.id ?? 0,
                            is_required: 0,
                            meta_data_name: category.category_name ?? '',
                            meta_data_option: [],
                            meta_data_type: MetaDataTypeEnum.text,
                            section: 'category',
                            updated_at: category.updated_at ?? '',
                            user_id: category.user_id ?? 0,
                            workspace_id: category.workspace_id ?? 0,
                        } as MetadataSelectionItem));

                        this.metaDataItems = [...this.metaDataItems, ...matchedCategories];
                    }
                    this.autoCompleteLoading = false;
                }
            });
    }




    /**
     * Returns an array of field section identifiers based on the currently active nested tab.
     * If the active tab is `DocumentTypeGroupEnum.Overview`, both `INDEX_FIELD` and `DOCUMENT_FIELD` are included.
     * Otherwise, only `INDEX_FIELD` is returned.
     *
     * @returns {string[]} An array of field section identifiers relevant to the active tab.
     */
    private getMetaDataSection(): string[] {
        const activeTab = this._tabStateService.getActiveTab() as DocumentTypeSectionEnum;
        if ([DocumentTypeSectionEnum.ContractControlColumns, DocumentTypeSectionEnum.DocumentColumns].includes(activeTab)) {
            return [FieldSection.DOCUMENT_FIELD];
        }

        if (this._tabStateService.getActiveNestedTab() === DocumentTypeGroupEnum.Overview) {
            return [FieldSection.INDEX_FIELD, FieldSection.DOCUMENT_FIELD];
        } else {
            switch (this._tabStateService.getNestedActiveTab()) {
                case DocumentTypeGroupEnum.DocumentField:
                    return [FieldSection.DOCUMENT_FIELD];
                default:
                    return [FieldSection.INDEX_FIELD];
            }
        }

    }




    /**
     * Updates the selector for a specific document type setting based on the provided form group.
     * Retrieves the active flag from the form's current value and passes it to the document type facade
     * to update the selector accordingly.
     *
     * @param form - The FormGroup containing the current document type setting values.
     */
    updateSelectorForSetting(form: FormGroup): void {
        const selector = this.getActiveFlag(form.value);
        this._documentTypeFacade.updateSelectorForSetting(selector);
    }




    /**
    * Get active flag based on the form values
    * This method checks the values of `document_type_setting` and `contract_type_setting`
    * in the provided form value and returns a string representing the active flag.
    * It is used to determine which settings are currently active.
    *
    * @param value - The form value containing settings.
    * @returns A string representing the active flag.
    */
    private getActiveFlag(value: IGeneralSettingsForm): string {
        if (value?.document_type_setting && value?.contract_type_setting) return '3';
        if (value?.document_type_setting) return '1';
        if (value?.contract_type_setting) return '2';
        return '0';
    }





    /**
     * Updates the layout settings for the current document type.
     * 
     * Subscribes to the `selectDocumentTypeLayoutSettings$` observable to retrieve the current
     * layout settings (`IDocumentTypesDto`) and passes them to the `updateLayoutSettings` method
     * of the `_documentTypeFacade`. The subscription is automatically completed after the first emission.
     */
    updateLayoutSettings(): void {
        this._documentTypeFacade.selectDocumentTypeLayoutSettings$.pipe(take(1)).subscribe((settings: IDocumentTypesDto) => {
            this._documentTypeFacade.updateLayoutSettings(settings);
        });
    }





    /**
     * Updates the selected categories for each section in the active tab.
     * 
     * If the active tab sections array is empty, displays an error message and returns.
     * For each section, checks if the given category (`selectedCategories`) exists:
     * - If it exists, removes it from the section's categories.
     * - If it does not exist, adds it to the section's categories.
     * 
     * @param selectedCategories - The category to add or remove from each section's categories.
     */
    updateSelectedCategories(selectedCategories: ICategory): void {
        const targetSectionNames = this.getCategoryTargetSections();
        const sectionsToUpdate = this.documentTypeSettings.sections.filter(section =>
            targetSectionNames.includes(section.name)
        );

        if (sectionsToUpdate.length === 0) {
            this._globalService.toastrError('Active tab not found');
            return;
        }

        sectionsToUpdate.forEach(section => {
            const sectionCategories = section.categories ?? [];
            const someCategoryIndex = sectionCategories.findIndex(cat => cat.category_id === selectedCategories.category_id);

            if (someCategoryIndex !== -1) {
                sectionCategories[someCategoryIndex].status = selectedCategories.status;
            } else {
                sectionCategories.push({ ...selectedCategories });
            }

            section.categories = sectionCategories;

            const selectedCategoryIds = section.selected_category_ids ?? [];
            if (selectedCategories.status) {
                section.selected_category_ids = selectedCategoryIds.includes(selectedCategories.category_id)
                    ? selectedCategoryIds
                    : [...selectedCategoryIds, selectedCategories.category_id];
            } else {
                section.selected_category_ids = selectedCategoryIds.filter(id => id !== selectedCategories.category_id);
            }
        });

        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }

    private getCategoryTargetSections(): DocumentTypeSectionEnum[] {
        const activeTab = this._tabStateService.getActiveTab();

        if ([
            DocumentTypeSectionEnum.ContractControlLayout,
            DocumentTypeSectionEnum.ContractControlCategory,
            DocumentTypeSectionEnum.ContractControlColumns
        ].includes(activeTab as DocumentTypeSectionEnum)) {
            return [
                DocumentTypeSectionEnum.ContractControlLayout,
                DocumentTypeSectionEnum.ContractControlCategory,
                DocumentTypeSectionEnum.ContractControlColumns
            ];
        }

        if ([
            DocumentTypeSectionEnum.DocumentLayout,
            DocumentTypeSectionEnum.DocumentCategory,
            DocumentTypeSectionEnum.DocumentColumns
        ].includes(activeTab as DocumentTypeSectionEnum)) {
            return [
                DocumentTypeSectionEnum.DocumentLayout,
                DocumentTypeSectionEnum.DocumentCategory,
                DocumentTypeSectionEnum.DocumentColumns
            ];
        }

        return [activeTab as DocumentTypeSectionEnum];
    }




    /**
     * Updates the columns for all active tab sections with the provided columns.
     * If no active tab sections are found, displays an error message.
     * After updating the columns, triggers an update of the layout settings in the state.
     *
     * @param columns - An array of `IField` objects representing the new columns to set for each active tab section.
     */
    updateColumns(columns: IField[]): void {
        if (this.activeTabSections.length === 0) {
            this._globalService.toastrError('Active tab not found');
            return;
        }

        this.activeTabSections.forEach(section => section.columns = columns);
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }




    /**
     * Removes a column at the specified index from each section in the active tab.
     * If no active tab sections are found, displays an error message.
     * After removing the column, updates the layout settings in the state.
     *
     * @param columnIndex - The index of the column to remove from each section.
     */
    removeColumn(columnIndex: number): void {
        if (this.activeTabSections.length === 0) {
            this._globalService.toastrError('Active tab not found');
            return;
        }
        this.activeTabSections.forEach(section => {
            section.columns = section.columns.filter((_, index) => index !== columnIndex);
        });
        this._documentTypeFacade.updateLayoutSettingsInState(this.documentTypeSettings);
    }

    private getFieldType(): FieldSection {
        const isOpenedFromDocumentTopSection = this._tabStateService.getActiveTab() === DocumentTypeSectionEnum.DocumentLayout && this._tabStateService.getNestedActiveTab() === DocumentTypeGroupEnum.TopSection;
        const sectionType = isOpenedFromDocumentTopSection ? FieldSection.INDEX_FIELD : FieldSection.DOCUMENT_FIELD;
        return sectionType;
    }

}
